import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";

interface BannerData {
  _id: string;
  bannerImage: string;
  heading: string;
  description?: string;
  __v?: number;
}

export default function AdminBannerEditor() {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [heading, setHeading] = useState("");
  const [description, setDescription] = useState("");
  const [bannerFile, setBannerFile] = useState<File | null>(null);

  const bannerId = "68efe4892d25c3fab3032123";

  const apiUrl = `${process.env.REACT_APP_LOCAL_URL}/api/banner-data/${bannerId}`;

  // Fetch banner
  const fetchBanner = async () => {
    try {
      setLoading(true);
      const res = await axios.get(apiUrl);
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        setBanner(data);
        setHeading(data.heading);
        setDescription(data.description || "");
      }
    } catch (error) {
      console.error("Error fetching banner:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanner();
  }, []);

  // Handle update (PATCH)
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!banner) return;

    try {
      const formData = new FormData();
      formData.append("heading", heading);
      formData.append("description", description);
      if (bannerFile) formData.append("bannerImage", bannerFile);

      await axios.patch(apiUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchBanner();
      setShowForm(false);
    } catch (error) {
      console.error("Error updating banner:", error);
    }
  };

  return (
    <div className="mt-6">
      <h2 className="font-lato-regular ">Section - 01 </h2>
      {banner && (
        <div className="p-4 relative w-full bg-white border mt-4 border-gray-200 rounded-xl overflow-hidden">
          <img
            src={`${process.env.REACT_APP_LOCAL_URL}/${banner.bannerImage}`}
            alt="Banner"
            className="w-full h-64 object-cover rounded mb-4"
          />
          <h3
            className="text-xl font-semibold mb-2 font-lato-regular"
            dangerouslySetInnerHTML={{ __html: banner.heading }}
          />
          <p
            className="text-gray-700 mb-4 font-lato-regular"
            dangerouslySetInnerHTML={{ __html: banner.description || "" }}
          />

          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-500 text-white px-2 py-1 rounded flex gap-2 justify-center items-center"
          >
            <Icon icon={FaRegEdit} />
            <span>Edit</span>
          </button>
        </div>
      )}

      {/* Modal Popup */}
      {showForm && banner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white w-full max-w-lg p-6 rounded-xl shadow-lg relative">
            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Heading */}
              <div>
                <label className="block font-semibold mb-1">Heading</label>
                <input
                  type="text"
                  value={heading}
                  onChange={(e) => setHeading(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded p-2"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block font-semibold mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  className="w-full border border-gray-300 rounded p-2"
                />
              </div>

              {/* Banner Image */}
              <div>
                <label className="block font-semibold mb-1">Banner Image</label>
                {banner.bannerImage && !bannerFile && (
                  <img
                    src={`${process.env.REACT_APP_LOCAL_URL}/${banner.bannerImage}`}
                    alt="Banner"
                    className="w-full h-40 object-cover rounded mb-2"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                  className="w-full border border-gray-300 rounded p-2"
                />
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
